/**
 * Completion reward: once every registered egg is found, the header logo keeps
 * a golden aura on every page load - the visible proof that someone finished
 * the hunt. The moment the last egg falls, there is a one-off celebration on
 * top of that egg's own effects.
 *
 * This module deliberately knows nothing about the engine (which imports it),
 * so the found-state check lives in engine.ts and in the navbar view.
 */

import { _x } from '@wordpress/i18n'
import { playConfetti } from './confetti'
import { showMessage } from './message'
import { playSound } from './sound'

const styleId = 'icf-easter-egg-crown-style'
const crownClass = 'icf-easter-egg-crown'
// Separate from the engine's found-record: that one grows egg by egg, this one
// remembers *which set* of eggs was already celebrated. It stores the slug
// signature, not just a flag, so adding a new egg later re-arms the payout -
// whoever was done before gets a second party once they find the newcomer.
const celebratedKey = 'icf-easter-eggs-completed'

// The link around the header logo (navbar/template.php), not the <img> itself:
// in dark mode .brand-image already carries filter: invert(1), and our
// drop-shadow would replace it and turn the logo black. On the wrapper the
// shadow is applied to the already-inverted child instead.
// Scoped to the navbar on purpose - .brand-image also exists in both footers.
const logoSelector = '.wp-block-yellowtree-navbar .navbar-brand'

// Injected from JS on purpose: PurgeCSS only scans php/html/jsx/tsx sources,
// so a class referenced solely in this .ts file would be purged.
// drop-shadow follows the alpha channel of the logo SVG, so the glow hugs the
// letters instead of a rectangle.
const css = `
.${crownClass} {
  animation: icf-easter-egg-crown 3.2s ease-in-out infinite;
}

@keyframes icf-easter-egg-crown {
  0%, 100% {
    filter:
      drop-shadow(0 0 3px rgba(255, 210, 122, 0.85))
      drop-shadow(0 0 9px rgba(255, 179, 71, 0.45));
  }
  50% {
    filter:
      drop-shadow(0 0 7px rgba(255, 210, 122, 1))
      drop-shadow(0 0 20px rgba(255, 179, 71, 0.65));
  }
}

/* the pulse is decoration - the glow itself stays, just without motion */
@media (prefers-reduced-motion: reduce) {
  .${crownClass} {
    animation: none;
    filter:
      drop-shadow(0 0 5px rgba(255, 210, 122, 0.9))
      drop-shadow(0 0 14px rgba(255, 179, 71, 0.5));
  }
}
`

function injectStyles() {
  if (document.getElementById(styleId)) return
  const style = document.createElement('style')
  style.id = styleId
  style.textContent = css
  document.head.appendChild(style)
}

/** Put the golden aura on the header logo. Safe to call more than once. */
export function applyCompletionGlow() {
  const logos = document.querySelectorAll<HTMLElement>(logoSelector)
  if (logos.length === 0) return
  injectStyles()
  for (const logo of logos) {
    logo.classList.add(crownClass)
  }
}

/** Take the aura off again, so a reset is visible without a reload. */
export function removeCompletionGlow() {
  for (const logo of document.querySelectorAll<HTMLElement>(
    `.${crownClass}`
  )) {
    logo.classList.remove(crownClass)
  }
}

// order-independent fingerprint of the celebrated egg set
function signature(slugs: string[]): string {
  return [...slugs].sort().join(',')
}

function alreadyCelebrated(slugs: string[]): boolean {
  try {
    return localStorage.getItem(celebratedKey) === signature(slugs)
  } catch {
    // storage unavailable (e.g. private mode) - the payout may repeat, which
    // is the friendlier failure than never paying out at all
    return false
  }
}

function markCelebrated(slugs: string[]) {
  try {
    localStorage.setItem(celebratedKey, signature(slugs))
  } catch {
    // see above
  }
}

/**
 * One-off payout for finding every egg: confetti, a chime and the closing
 * message, plus the permanent logo glow. Repeats only if the set of eggs has
 * changed since the last celebration.
 *
 * @param foundSlugs every found egg slug - the caller checks completeness
 */
export function celebrateCompletion(foundSlugs: string[]) {
  if (alreadyCelebrated(foundSlugs)) {
    // a returning visitor still gets the glow, just no second party
    applyCompletionGlow()
    return
  }
  markCelebrated(foundSlugs)

  playConfetti({ particleCount: 260, durationMs: 5000 })
  playSound({ synth: 'chime' })
  applyCompletionGlow()

  // The triggering egg usually shows a message of its own, and showMessage
  // only ever keeps one on screen. So the payout waits a moment instead of
  // cutting off the message that earned it.
  setTimeout(() => {
    showMessage({
      text: _x(
        'That was the last one - you found them all. The logo up there now carries a golden glow, just for you.',
        'Easter Egg Completion Message',
        'icf'
      ),
      // the subsetted kit has no trophy/crown/star - sun-bright is the most
      // golden thing available
      icon: 'fa-sun-bright',
      durationMs: 12000
    })
  }, 4000)
}
